/**
 * Special sort type identifiers used by table column sorting.
 * @see dateSorting.js
 */
export const SORT_TYPES = {
  HOUR_OF_DAY: "hourOfDay",
  DATE: "date",
  TENURE: "tenure",
  WEEK_RANGE: "week-range",
  WEEK: "weekRange",
  QUARTER: "quarter",
  MONTH_YEAR: "monthYear",
  DAY_OF_WEEK: "dayOfWeek",
};

/**
 * Human-readable metadata for each special sort type.
 * `fieldTypes` lists column meta types that automatically trigger the sorter.
 */
export const SORT_TYPE_DEFINITIONS = {
  [SORT_TYPES.HOUR_OF_DAY]: {
    description:
      "Sorts hour-of-day values chronologically within AM and PM groups.",
    examples: ["8 AM", "11 AM", "1 PM", "5 PM"],
    fieldTypes: [SORT_TYPES.HOUR_OF_DAY],
  },
  [SORT_TYPES.DATE]: {
    description: "Sorts parseable date strings chronologically.",
    examples: ["2024-01-15", "2024-06-01", "01/15/2024"],
    fieldTypes: [SORT_TYPES.DATE],
  },
  [SORT_TYPES.TENURE]: {
    description:
      "Sorts employee tenure buckets in a predefined business order.",
    examples: ["0-4 Months", "12+ Months", "48+ Months", "Total"],
    fieldTypes: [SORT_TYPES.TENURE],
  },
  [SORT_TYPES.WEEK_RANGE]: {
    description:
      "Sorts week range labels by the start date (hyphen-separated).",
    examples: ["02/01/2022 - 02/07/2022", "03/01/2022 - 03/07/2022"],
    fieldTypes: [SORT_TYPES.WEEK_RANGE],
  },
  [SORT_TYPES.WEEK]: {
    description: "Sorts week range labels by the start date (space-separated).",
    examples: ["02/01/2022 02/07/2022", "03/01/2022 03/07/2022"],
    fieldTypes: [SORT_TYPES.WEEK],
  },
  [SORT_TYPES.QUARTER]: {
    description:
      "Sorts fiscal or calendar quarter labels chronologically by year, then quarter.",
    examples: ["Q2 2026", "2022 Q1", "Q4 - 2011", "Q1-2024"],
    fieldTypes: [SORT_TYPES.QUARTER],
  },
  [SORT_TYPES.MONTH_YEAR]: {
    description:
      "Sorts month-year labels chronologically, supporting full and abbreviated month names.",
    examples: ["September 2025", "Sep 2025", "September-2025", "Sep-2025"],
    fieldTypes: [
      SORT_TYPES.MONTH_YEAR,
      "MonthYear",
      "month-year",
      "full-month-year",
      "MMM yyyy",
      "MMMM yyyy",
    ],
  },
  [SORT_TYPES.DAY_OF_WEEK]: {
    description:
      "Sorts day-of-week labels from Sunday through Saturday, including abbreviations.",
    examples: ["Sunday", "Mon", "Wednesday", "Sat"],
    fieldTypes: [SORT_TYPES.DAY_OF_WEEK, "day-of-week"],
  },
};

export const FIELD_TYPE_TO_SORT_TYPE = Object.entries(
  SORT_TYPE_DEFINITIONS
).reduce((acc, [sortType, { fieldTypes }]) => {
  fieldTypes.forEach((fieldType) => {
    acc[fieldType] = sortType;
  });
  return acc;
}, {});

export const MONTH_YEAR_FORMATS = [
  "MMMM yyyy",
  "MMM yyyy",
  "MMMM-yyyy",
  "MMM-yyyy",
];

export const TENURE_ORDER = [
  "0-4 Months",
  "4+ Months",
  "4-8 Months",
  "8-12 Months",
  "12+ Months",
  "12-24 Months",
  "24-36 Months",
  "36-48 Months",
  "48+ Months",
  "Total",
];
